/* eslint-disable react/no-danger */
import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import {
	COMPANY_ADDRESS,
	COMPANY_LEGAL_NAME,
	COMPANY_NAME,
	COMPANY_URL,
	DISPUTE_LINK,
	EXTERNAL_COOKIES_LINKS,
	EXTERNAL_LINKS,
	SUPPORT_EMAIL,
	TAX_ID
} from '@/config';
import routes from '@/routes';

import Spinner from '../Spinner';
import './styles.css';
import { getLink, mailTo } from './utils';

function getCookiesLinks() {
	let links = '';
	for (const link of EXTERNAL_COOKIES_LINKS) {
		links += `<li><a href="${link.url}" target="_blank">${link.label}</a></li>`;
	}
	return links;
}

function Legals({ title, subtitle, content }) {
	const { t } = useTranslation();

	const formattedContent = useMemo(() => {
		if (!content) {
			return '';
		}
		const supportLink = mailTo(SUPPORT_EMAIL);
		const disputeLink = getLink(DISPUTE_LINK);
		const websiteLink = getLink(COMPANY_URL);
		const cookiesPolicyLink = getLink(
			routes.cookiesPolicy,
			t('navigation.cookies')
		);
		const privacyPolicyLink = getLink(
			routes.privacyPolicy,
			t('navigation.privacy')
		);

		const replacements = {
			companyAddress: COMPANY_ADDRESS,
			companyName: COMPANY_NAME,
			companyLegalName: COMPANY_LEGAL_NAME,
			siteName: COMPANY_URL,
			taxId: TAX_ID,
			websiteLink,
			supportLink,
			disputeLink,
			cookiesPolicyLink,
			privacyPolicyLink,
			externalLinks: EXTERNAL_LINKS,
			cookiesLinks: getCookiesLinks()
		};

		return content.replace(
			/{([^{}:]+)(?::\s*([^{}:]+))?}/g,
			(match, p1, p2) => {
				if (p2) {
					return replacements.externalLinks[p2] || match;
				}
				return replacements[p1] || match;
			}
		);
	}, [content]);

	return (
		<div className="flex flex-col">
			<section className="relative mb-12 flex h-56 flex-col items-center justify-center gap-2 overflow-hidden bg-grey-100 md:mb-20 md:h-[360px] md:gap-6">
				<div className="absolute -bottom-24 z-0 h-40 w-40 rounded-full bg-red blur-[50px] md:-bottom-96 md:block md:h-[468px] md:w-[468px] md:blur-[150px]" />
				<div className="container z-10 flex flex-col gap-3 text-center">
					<h1 className="mx-auto max-w-[814px] font-title text-[32px] font-bold leading-10 text-black md:text-[56px] md:leading-[64px]">
						{title}
					</h1>
					<h2 className="font-serif text-sm leading-6 text-black md:text-base md:leading-7">
						{subtitle}
					</h2>
				</div>
			</section>
			<main className="container relative mb-[149px] flex min-h-[350px] justify-center self-center md:mb-[119px]">
				{formattedContent ? (
					<div className="legal-content text-grey-500">
						<div dangerouslySetInnerHTML={{ __html: formattedContent }} />
					</div>
				) : (
					<Spinner size={50} />
				)}
			</main>
		</div>
	);
}

export default Legals;
